/**
 * @typedef Navigation
 * @type {object}
 * @property {string|undefined} from
 * @property {string|undefined} to
 * @property {boolean} [skipHistory]
 */

/**
 * @typedef RoutePage
 * @type {object}
 * @property {string} page
 * @property {object} params
 */

export default class NavigationStack {
  constructor() {
    /**
     * @type {RoutePage[]}
     */
    this.navStack = [];
    /**
     * @type {{[key:string]: boolean}}
     */
    this.skipNav = {};
  }

  /**
   * @returns {number}
   */
  get length() {
    return this.navStack.length;
  }

  /**
   * @param {Navigation} nav
   */
  addSkipNavigation(nav) {
    if (nav.skipHistory === true || nav.skipHistory === false) {
      this.skipNav[`${nav.from}:${nav.to}`] = nav.skipHistory;
    }
  }

  /**
   * @param {Navigation} nav
   * @returns {Navigation} nav
   */
  _reverseNavigation(nav) {
    return {
      from: nav.to,
      to: nav.from
    };
  }

  /**
   * @param {Navigation} nav
   * @returns {boolean}
   */
  isSkipNavigation(nav) {
    return this.skipNav[`${nav.from}:${nav.to}`] === true;
  }

  /**
   * @returns {Navigation} nav
   */
  lastNavigation() {
    const navCount = this.navStack.length;
    let from;
    let to;
    if (navCount === 1) {
      to = this.navStack[0];
    } else if (navCount > 1) {
      to = this.navStack[navCount-1];
      from = this.navStack[navCount-2];
    }
    return this.createNavigation(from, to);
  }

  /**
   * @param {string} page
   * @param {object} params
   * @returns {RoutePage}
   */
  createRoute(page, params = {}) {
    return {
      page,
      params
    };
  }

  /**
   * @param {RoutePage|undefined} routeFrom
   * @param {RoutePage|undefined} routeTo
   * @returns {Navigation}
   */
  createNavigation(routeFrom, routeTo) {
    return {
      from: routeFrom ? routeFrom.page : undefined,
      to: routeTo ? routeTo.page : undefined
    };
  }

  /**
   * @param {RoutePage} route
   */
  push(route) {
    if (!this.top() || this.top()?.page !== route.page) {
      this.navStack.push(route);
    }
  }

  /**
   * @param {RoutePage} route
   */
  replace(route) {
    if (!this.top() || this.top()?.page !== route.page) {
      this.navStack[this.navStack.length - 1] = route;
    }
  }

  /**
   * @returns {RoutePage|undefined}
   */
  pop() {
    return this.navStack.pop();
  }

  /**
   * @returns {RoutePage|undefined}
   */
  top() {
    return this.navStack.length > 0 ? this.navStack[this.navStack.length - 1] : undefined;
  }

  /**
   * @param {Navigation} newNav
   * @returns {boolean}
   */
  isBackwardNavigation(newNav) {
    const lastNav = this.lastNavigation();
    return lastNav && newNav.from === lastNav.to && newNav.to === lastNav.from;
  }

  /**
   * @param {RoutePage} routeFrom
   * @param {RoutePage} routeTo
   * @returns {RoutePage|undefined}
   */
  update(routeFrom, routeTo) {
    const nav = this.createNavigation(routeFrom, routeTo);
    if (this.isBackwardNavigation(nav)) {
      while(this.isSkipNavigation(this._reverseNavigation(this.lastNavigation()))) {
        this.pop();
      }
      this.pop();
    } else {
      this.push(routeTo);
    }
    return this.top();
  }

  clear() {
    this.navStack = [];
  }

  /**
   * @param {RoutePage} route
   */
  replaceRoute(route) {
    if (this.navStack.length > 0) {
      this.navStack[this.navStack.length - 1] = route;
    } else {
      this.push(route);
    }
  }

  /**
   * Clear the router stack until given page is found on router stack.
   * @param {string} targetPage
   */
  clearUntil(targetPage) {

    if (this.navStack.find(route=>route.page === targetPage)) {
      let currentRoute = null;
      do {
        currentRoute = this.navStack.pop();
      } while (currentRoute && currentRoute.page != targetPage);
    }
  }
}
